import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { hasPermission } from 'utils/utils';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { notification } from 'antd';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { deleteRequest } from 'api/apiClient';
import ProfileEditModal from './ProfileEditModal';
import { fetchVisits } from 'store/slices/visitsSlice';
import { searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { useTranslation } from 'react-i18next';
import STATUS from 'constants/status';

interface ProfileDrawerHeaderProps {
    onClose: () => void;
    setDrawerLoading?: (loading: boolean) => void;
}

const ProfileDrawerHeader: React.FC<ProfileDrawerHeaderProps> = ({ onClose, setDrawerLoading }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const { selectedVisit, selectedVisitor } = useSelector((state: RootState) => state.visits);

    const hasVisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'visitor:w');
    const hasAllvisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');
    const [isProfileEditModalOpen, setIsProfileEditModalOpen] = useState(false);

    const confirmModal = useConfirmModal();
    const { t } = useTranslation();

    const onDeleteClick = () => {
        confirmModal({
            title: t('visitors.profile.confirmDelete.title'),
            content: t('visitors.profile.confirmDelete.message'),
            okText: t('visitors.profile.confirmDelete.ok'),
            cancelText: t('visitors.profile.confirmDelete.cancel'),
            onOk: async () => {
                try {
                    if (selectedVisit && selectedVisit.visitors.length > 1) {
                        await deleteRequest(`/orgs/${orgId}/visitor/${selectedVisit.id}/visitors/${selectedVisitor?.id}`);
                        notification.success({
                            message: t('notification.success'),
                            description: t('visitors.profile.notification.deleteSuccess'),
                            placement: 'bottomRight',
                        });
                        await dispatch(fetchVisits({ orgId }));
                        dispatch(searchVisitorsToday({ orgId }));
                        onClose();
                    } else {
                        notification.error({
                            message: 'Error',
                            description: t('visitors.profile.notification.deleteError'),
                            placement: 'bottomRight',
                        });
                    }
                } catch (error) {
                    console.log("Failed to delete visitor.");
                }
            },
        });
    };

    return (
        <>
            <OpSpace>
                {
                    (hasAllvisitorsWrite || (hasVisitorsWrite && globalUserId === selectedVisit?.host.userId)) && (
                        <>
                            {selectedVisitor?.status === STATUS.PENDING.id && (
                                <OpButton type="primary" danger ghost onClick={onDeleteClick}>
                                    {t('visitors.profile.header.delete')}
                                </OpButton>
                            )}
                            {selectedVisitor?.status !== STATUS.DENIED_ENTRY.id && (
                                <OpButton type="primary" ghost onClick={() => setIsProfileEditModalOpen(true)}>
                                    {t('visitors.profile.header.edit')}
                                </OpButton>
                            )}
                        </>
                    )}
            </OpSpace>
            {(isProfileEditModalOpen) && (
                <ProfileEditModal
                    open={isProfileEditModalOpen}
                    onClose={() => setIsProfileEditModalOpen(false)}
                    setDrawerLoading={setDrawerLoading}
                />
            )}
        </>
    );
}

export default ProfileDrawerHeader;